import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import FAQSection from "@/components/landing/FAQSection";
import { Button } from "@/components/ui/button";
import { ShopHeader } from "@/components/shop/ShopHeader";
import { Footer } from "@/components/shared/Footer";
import { SEOHead } from "@/components/SEOHead";
import {
  Leaf,
  Wifi,
  Sparkles,
  Camera,
  Brain,
  TrendingUp,
  Droplets,
  Thermometer,
  HeadphonesIcon,
  Shield,
  Truck,
  ArrowRight,
  Check,
  Play,
  Zap,
  Clock,
  BarChart3,
  Target,
  TreeDeciduous,
  Heart,
  Wallet,
  AlertTriangle,
  BrainCircuit,
  MessageCircleHeart,
  ScanEye,
  MessageSquare,
  Sun,
  Battery,
} from "lucide-react";
import forestBackground from "@/assets/last-hero.jpg";
import planteProbleme from "@/assets/plante-probleme.webp";
import doigtTerre from "@/assets/doigt-terre-optimized.webp";
import produitVisuAsset from "@/assets/produit-visu.mp4.asset.json";
import cynthiaSimple from "@/assets/cynthia-simple.webp";
import packDuoAsset from "@/assets/pack-duo.mp4.asset.json";
import DemoSection from "@/components/landing/DemoSection";
import { LazyVideo } from "@/components/LazyVideo";

import MobileQuickNav from "@/components/landing/MobileQuickNav";
import FeatureBrainCapture from "@/components/landing/FeatureBrainCapture";
import FeatureWaterCapture from "@/components/landing/FeatureWaterCapture";
import FeatureChatCapture from "@/components/landing/FeatureChatCapture";
import FeatureScanCapture from "@/components/landing/FeatureScanCapture";
const Index = () => {
  const navigate = useNavigate();
  const fadeInUp = {
    hidden: {
      opacity: 0,
      y: 30,
    },
    visible: {
      opacity: 1,
      y: 0,
    },
  };
  const staggerContainer = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };
  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead
        title="CynthIA - Boîtiers IoT et IA pour un jardin intelligent"
        description="Ne devinez plus les besoins de vos plantes, écoutez-les. CynthIA combine boîtiers connectés et intelligence artificielle pour optimiser votre jardinage et économiser jusqu'à 60% d'eau."
        canonical="https://cynthia.fr/"
        ogImage="https://storage.googleapis.com/gpt-engineer-file-uploads/U8VjKK4T3eRZuurQJqXLO5gB10r1/social-images/social-1766066019739-logo cynthia.jpg"
      />
      <ShopHeader />

      {/* ================= HERO SECTION (OPTIMISÉE MOBILE : ESPACE AGRANDI) ================= */}
      <link rel="preload" as="image" href={forestBackground} type="image/webp" />
      <section
        className="relative min-h-[90vh] flex items-center justify-center bg-cover bg-center bg-no-repeat overflow-hidden"
        style={{
          backgroundImage: `url(${forestBackground})`,
        }}
      >
        {/* OVERLAY : On garde le dégradé qui protège la lisibilité du texte en haut et en bas */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/90 from-0% via-transparent via-50% to-black/90 to-100%" />

        <div className="container mx-auto px-4 py-12 relative z-10 h-full flex flex-col justify-center">
          <motion.div
            className="max-w-5xl mx-auto text-center flex flex-col items-center w-full mt-6 md:mt-0"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            {/* 1. Badge Tech */}
            <motion.div
              variants={fadeInUp}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-950/60 backdrop-blur-md border border-emerald-500/30 mb-5 md:mb-6"
            >
              <Sparkles className="text-emerald-400" style={{ width: 12, height: 12 }} />
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-emerald-100">
                IA Gemini Intégrée
              </span>
            </motion.div>

            {/* 2. Titre */}
            <motion.h1
              variants={fadeInUp}
              className="text-5xl sm:text-6xl md:text-7xl font-extrabold text-white mb-0 tracking-tight leading-[1.05] [text-shadow:_0_2px_12px_rgb(0_0_0_/_80%)]"
            >
              Le jardinage <br />
              <span className="text-emerald-400">sans le stress.</span>
            </motion.h1>

            {/* 3. ESPACE VIDE AGRANDI (Mobile: h-[22rem] / Desktop: h-96)
                Cet espace est crucial pour laisser respirer ton boîtier sur l'image de fond. */}
            <div className="h-[22rem] md:h-96 w-full pointer-events-none" aria-hidden="true" />

            {/* 4. Tagline Hardware */}
            <motion.h2
              variants={fadeInUp}
              className="text-xl md:text-3xl font-light text-slate-200 mb-8 max-w-sm md:max-w-xl mx-auto [text-shadow:_0_2px_8px_rgb(0_0_0_/_90%)] leading-snug"
            >
              <span className="block mb-2">Ne cherchez plus l'expert.</span>
              <strong className="text-white font-bold border-b-2 border-emerald-500 pb-1">Plantez-le.</strong>
            </motion.h2>

            {/* 5. CTA */}
            <motion.div variants={fadeInUp} className="flex flex-col items-center gap-4 w-full px-2">
              <Button
                size="lg"
                onClick={() => {
                  navigate("/shop");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="w-full sm:w-auto text-lg px-8 py-7 bg-white hover:bg-emerald-50 text-emerald-950 font-bold shadow-[0_0_40px_-10px_rgba(255,255,255,0.3)] rounded-full transition-all transform hover:scale-105"
              >
                Commander mon CynthIA
                <ArrowRight className="ml-2 text-emerald-600 w-5 h-5" />
              </Button>

              {/* Trust indicators */}
              <div className="flex flex-wrap justify-center gap-3 mt-1 md:mt-2">
                <div className="flex items-center gap-2 text-white/90 text-xs sm:text-sm font-medium bg-black/50 px-4 py-2 rounded-full border border-white/10 backdrop-blur-md">
                  <Shield className="text-emerald-400 w-3.5 h-3.5" />
                  <span>
                    Satisfait ou remboursé <span className="text-white font-bold">30 jours</span>
                  </span>
                </div>
                <div className="flex items-center gap-2 text-white/90 text-xs sm:text-sm font-medium bg-black/50 px-3 py-2 rounded-full border border-white/10 backdrop-blur-md">
                  <span className="text-sm leading-none">🇫🇷</span>
                  <span>Conçu & fabriqué en France</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ================= SUPER-SECTION : LE PROBLÈME (FUSION) ================= */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-5 md:px-4">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center max-w-6xl mx-auto">
            {/* 1. IMAGE (LA PREUVE SCIENTIFIQUE) */}
            <motion.div
              className="order-2 lg:order-1"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="rounded-[2rem] shadow-xl aspect-[4/5] md:aspect-square w-full max-w-md mx-auto relative overflow-hidden border border-slate-100 group">
                <img
                  src={doigtTerre}
                  alt="Coupe transversale d'un pot montrant la terre sèche en surface et noyée en profondeur"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  width={400}
                  height={500}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </motion.div>

            {/* 2. TEXTE (STYLE ORIGINEL CONSERVÉ) */}
            <motion.div
              className="order-1 lg:order-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {/* Badge */}
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-red-100 mb-6 border border-yellow-200">
                <AlertTriangle className="w-3 h-3 text-red-600 mr-2" />
                <span className="text-xs font-bold uppercase tracking-wider text-red-700">Le Constat</span>
              </div>

              <h2 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6 leading-tight">
                Pourquoi vos plantes meurent-elles malgré tout votre amour ?
              </h2>

              <p className="text-lg md:text-xl text-slate-600 mb-10 leading-relaxed">
                Vous investissez du temps et de l'énergie. Pourtant, le résultat est souvent la déception. Ce n'est pas
                de votre faute :{" "}
                <span className="font-semibold text-slate-900">c'est le jardinage "à l'aveugle" qui est coupable.</span>
              </p>

              <div className="space-y-8">
                {/* Argument 1 : L'Illusion */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mt-1">
                    <ScanEye className="text-amber-600" style={{ width: 20, height: 20 }} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">L'Illusion de la Surface</h3>
                    <p className="text-lg text-slate-600 leading-relaxed">
                      Regardez l'image en dessous. La surface peut être sèche comme un désert alors que le fond est un
                      marécage.
                      <span className="font-medium text-slate-900">
                        {" "}
                        En vous fiant à votre doigt, vous noyez vos racines en pensant les sauver.
                      </span>
                    </p>
                  </div>
                </div>

                {/* Argument 2 : Le Pourrissement */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mt-1">
                    <Droplets className="text-red-600" style={{ width: 20, height: 20 }} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">Le Pourrissement Invisible</h3>
                    <p className="text-lg text-slate-600 leading-relaxed">
                      <span className="font-medium text-slate-900">67% des plantes meurent d'un excès d'eau</span>, pas
                      d'un manque. Quand les feuilles jaunissent, l'asphyxie racinaire a commencé depuis des semaines.
                      Il est souvent trop tard.
                    </p>
                  </div>
                </div>

                {/* Argument 3 : La Charge Mentale */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mt-1">
                    <BrainCircuit className="text-purple-600" style={{ width: 20, height: 20 }} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">La Charge Mentale</h3>
                    <p className="text-lg text-slate-600 leading-relaxed">
                      "Trop d'eau ? Pas assez ?" Le doute permanent transforme votre havre de paix en source de stress.
                      Arrêtez de deviner, commencez à savoir.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ================= SECTION SOLUTION (NOIR/DARK MODE - RÉVÉLATION) ================= */}
      <section className="py-16 md:py-32 bg-slate-950 text-white relative overflow-hidden">
        {/* Gradients subtils pour la profondeur */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-500/10 blur-[100px] rounded-full pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-500/10 blur-[100px] rounded-full pointer-events-none" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-3xl mx-auto text-center mb-10 md:mb-16"
            initial={{
              opacity: 0,
              y: 20,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.6,
            }}
          >
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 mb-4 md:mb-6 border border-white/20 backdrop-blur-md">
              <Sparkles
                className="text-emerald-400 mr-2"
                style={{
                  width: 14,
                  height: 14,
                }}
              />
              <span className="text-[10px] md:text-xs font-bold uppercase tracking-wider text-emerald-100">
                La Solution CynthIA
              </span>
            </div>

            <h2 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white mb-4 md:mb-6 leading-tight tracking-tight">
              Enfin, votre jardin <br />
              <span className="text-emerald-400">vous parle</span>
            </h2>

            <p className="text-base md:text-xl text-slate-400 leading-relaxed px-2">
              Accédez à votre <span className="font-semibold text-white">Expert Horticole Personnel</span> disponible{" "}
              <span className="font-semibold text-white">24/7</span>. <br className="hidden md:block" /> CynthIA
              traduit les signes vitaux invisibles de vos plantes en langage clair.
            </p>
            <p className="text-xl text-emerald-400 mt-3 font-medium">Protégez jusqu'à 10m² de votre jardin !*</p>
          </motion.div>

          {/* Hero Shot Produit */}
          <motion.div
            className="relative max-w-4xl mx-auto mb-12 md:mb-20"
            initial={{
              opacity: 0,
              scale: 0.95,
            }}
            whileInView={{
              opacity: 1,
              scale: 1,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.8,
            }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-emerald-500/20 blur-[60px] md:blur-[100px] rounded-full -z-10" />

            <div className="relative rounded-2xl md:rounded-3xl shadow-2xl border border-white/10 overflow-hidden aspect-[4/3] md:aspect-[21/9] group">
              <LazyVideo src={produitVisuAsset.url} className="w-full h-full" />
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12 max-w-6xl mx-auto">
            {/* Carte 1 */}
            <motion.div
              className="bg-white/5 p-6 md:p-8 rounded-2xl md:rounded-3xl border border-white/10 shadow-lg hover:bg-white/10 transition-colors"
              initial={{
                opacity: 0,
                y: 20,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
              }}
              transition={{
                duration: 0.5,
                delay: 0.1,
              }}
            >
              <div className="flex items-center gap-4 mb-4 md:block md:mb-6">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-emerald-500/20 flex items-center justify-center text-emerald-400 flex-shrink-0">
                  <Wifi className="w-6 h-6 md:w-7 md:h-7" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-white md:mb-3">
                  L'Analyse Micro-Climatique{" "}
                  <span className="text-emerald-400/60 md:block text-sm font-medium ml-2 md:ml-0 md:mt-1">
                    Les Yeux
                  </span>
                </h3>
              </div>
              <p className="text-slate-400 leading-relaxed text-sm">
                Votre boîtier <strong>CynthIA</strong> capture ce que vos yeux ne voient pas : l'humidité exacte du sol,
                la température et l'air.
                <span className="block mt-2 font-semibold text-emerald-300">
                  La fondation scientifique d'une culture saine.
                </span>
              </p>
            </motion.div>

            {/* Carte 2 */}
            <motion.div
              className="bg-white/5 p-6 md:p-8 rounded-2xl md:rounded-3xl border border-white/10 shadow-lg hover:bg-white/10 transition-colors"
              initial={{
                opacity: 0,
                y: 20,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
              }}
              transition={{
                duration: 0.5,
                delay: 0.2,
              }}
            >
              <div className="flex items-center gap-4 mb-4 md:block md:mb-6">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-blue-500/20 flex items-center justify-center text-blue-400 flex-shrink-0">
                  <BrainCircuit className="w-6 h-6 md:w-7 md:h-7" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-white md:mb-3">
                  L'Intelligence Prédictive{" "}
                  <span className="text-blue-400/60 md:block text-sm font-medium ml-2 md:ml-0 md:mt-1">Le Cerveau</span>
                </h3>
              </div>
              <p className="text-slate-400 leading-relaxed text-sm">
                <strong>Notre IA</strong> croise vos mesures avec la météo locale et les besoins de +200 espèces. Elle
                anticipe la pluie et <strong>prédit les besoins futurs</strong>.
                <span className="block mt-2 font-semibold text-blue-300">Pour maximiser vos résultats.</span>
              </p>
            </motion.div>

            {/* Carte 3 */}
            <motion.div
              className="bg-white/5 p-6 md:p-8 rounded-2xl md:rounded-3xl border border-white/10 shadow-lg hover:bg-white/10 transition-colors"
              initial={{
                opacity: 0,
                y: 20,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
              }}
              transition={{
                duration: 0.5,
                delay: 0.3,
              }}
            >
              <div className="flex items-center gap-4 mb-4 md:block md:mb-6">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-amber-500/20 flex items-center justify-center text-amber-400 flex-shrink-0">
                  <MessageCircleHeart className="w-6 h-6 md:w-7 md:h-7" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-white md:mb-3">
                  L'Expert Personnel 24/7{" "}
                  <span className="text-amber-400/60 md:block text-sm font-medium ml-2 md:ml-0 md:mt-1">La Voix</span>
                </h3>
              </div>
              <p className="text-slate-400 leading-relaxed text-sm">
                Au-delà des alertes, accédez à des <strong>recommandations IA</strong>. Discutez avec{" "}
                <strong>CynthIA</strong> ou diagnostiquez une maladie par photo.
                <span className="block mt-2 font-semibold text-amber-300">
                  Libérez-vous du stress et profitez de votre jardin !
                </span>
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ================= SECTION IA (BLANC - SCIENCE) ================= */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-5 md:px-4">
          <div className="max-w-4xl mx-auto text-center mb-20">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-purple-100 mb-6 border border-purple-200">
              <Sparkles
                className="text-purple-600 mr-2"
                style={{
                  width: 14,
                  height: 14,
                }}
              />
              <span className="text-xs font-bold uppercase tracking-wider text-purple-800">
                L'Intelligence Artificielle CynthIA
              </span>
            </div>

            <h2 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6 leading-tight">
              Plus de doutes. <br />
              <span className="text-purple-600">Juste des réponses.</span>
            </h2>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Propulsée par Gemini, CynthIA ne se contente pas de mesurer. Elle voit vos problèmes et répond à vos
              questions. <br className="hidden md:block" />
              <span className="block mt-4 text-purple-700 font-medium">
                Ne soyez plus jamais bloqué.
                <span className="block mt-1 text-slate-500 font-normal italic text-lg">
                  (Fini le temps perdu sur Facebook...)
                </span>
              </span>
            </p>
          </div>

          <div className="space-y-24 max-w-6xl mx-auto">
            {/* 1. Le Cerveau Croisé — Desktop: texte gauche, image droite 
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              <div className="w-full lg:w-1/2 order-1 lg:order-1">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600">
                    <BrainCircuit className="w-6 h-6" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">Le Cerveau Croisé</h3>
                </div>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Fini l'arrosage au hasard. L'IA croise météo et besoins réels pour vous dire <em>exactement</em> quand
                  agir. Ne le faites plus « parce que c'est mardi », mais parce que c'est nécessaire.
                  <strong className="block mt-2 text-purple-700">Résultat : Économisez jusqu'à 80% d'eau.</strong>
                </p>
              </div>
              <div className="w-full lg:w-1/2 order-2 lg:order-2">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="hover:scale-[1.02] transition-transform duration-500"
                >
                  <FeatureBrainCapture />
                </motion.div>
              </div>
            </div>*/}

            {/* 2. L'Œil Expert — Desktop: image gauche, texte droite (zigzag) */}
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              <div className="w-full lg:w-1/2 order-1 lg:order-1">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600">
                    <ScanEye className="w-6 h-6" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">L'Œil Expert</h3>
                </div>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Une feuille jaune ? <strong>Une simple photo suffit.</strong>
                  <br />
                  L'IA identifie instantanément la maladie (ou la carence) et vous donne le remède naturel adapté avant
                  que le problème ne se propage.
                </p>
              </div>
              <div className="w-full lg:w-1/2 order-2 lg:order-2">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="hover:scale-[1.02] transition-transform duration-500"
                >
                  <FeatureScanCapture />
                </motion.div>
              </div>
            </div>

            {/* 3. Assistant Botanique — Desktop: texte gauche, image droite (zigzag) */}
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              <div className="w-full lg:w-1/2 order-1 lg:order-2">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600">
                    <MessageSquare className="w-6 h-6" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">L'Assistant Botanique</h3>
                </div>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Discutez avec un expert qui connaît <em>vos</em> plantes.
                  <br />« Quand tailler mes rosiers ? », « Pourquoi mon Ficus fane ? ».
                  <strong> CynthIA vous répond instantanément</strong> et vous guide geste par geste.
                </p>
              </div>
              <div className="w-full lg:w-1/2 order-2 lg:order-1">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="hover:scale-[1.02] transition-transform duration-500"
                >
                  <FeatureChatCapture />
                </motion.div>
              </div>
            </div>

            {/* Réassurance (Reste inchangée car elle était bien) */}
            <div className="bg-slate-50 rounded-3xl p-8 md:p-12 shadow-sm border border-slate-200 mt-20">
              <div className="text-center mb-10">
                <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">Conçu pour votre vie réelle</h3>
                <p className="text-slate-500">Une technologie qui s'adapte à vous, pas l'inverse.</p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 md:gap-12">
                <div className="flex gap-5">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white flex items-center justify-center border border-slate-100">
                    <Sun className="w-6 h-6 text-slate-600" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-2">Hybride & Polyvalent</h4>
                    <p className="text-slate-600 leading-relaxed">
                      Salon ou Potager ? CynthIA s'adapte. Son design étanche IP67 résiste à la pluie, tandis que son
                      esthétique épurée s'intègre parfaitement à votre décoration intérieure.
                    </p>
                  </div>
                </div>

                <div className="flex gap-5">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white flex items-center justify-center border border-slate-100">
                    <Battery className="w-6 h-6 text-slate-600" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-2">Liberté Totale</h4>
                    <p className="text-slate-600 leading-relaxed">
                      Installez-le et oubliez-le. Avec plus de <strong>200 jours d'autonomie</strong>, il veille sur vos
                      plantes pendant vos vacances et vos semaines chargées sans jamais faiblir.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= DEMO SECTION ================= */}
      <DemoSection />

      {/* ================= PRICING SECTION (GRIS LÉGER) ================= */}
      <section className="py-20 md:py-32 bg-slate-50 border-t border-slate-200" id="offre">
        <div className="container mx-auto px-5 md:px-4 relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6">
              Choisissez votre formule <br />
              <span className="text-emerald-600">CynthIA</span>
            </h2>
            <p className="text-xl text-slate-500">Achat unique — Expert Horticole inclus à vie, sans abonnement.</p>
          </motion.div>

          {/* GRID DES PRIX */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto items-start mb-16">
            {/* CARTE 1 : SOLO */}
            <motion.div
              className="bg-white rounded-3xl border border-slate-200 shadow-xl relative overflow-hidden hover:border-emerald-200 transition-all flex flex-col h-full"
              initial={{
                opacity: 0,
                x: -20,
              }}
              whileInView={{
                opacity: 1,
                x: 0,
              }}
              viewport={{
                once: true,
              }}
            >
              <div className="h-48 bg-slate-50 flex items-center justify-center border-b border-slate-100 relative overflow-hidden">
                <img
                  src={cynthiaSimple}
                  alt="Pack Solo CynthIA - Boîtier CynthIA"
                  className="w-full h-full object-cover"
                  width={400}
                  height={192}
                  loading="lazy"
                  decoding="async"
                />
              </div>

              <div className="p-6 md:p-8 flex flex-col flex-grow">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Pack Découverte</h3>
                <p className="text-xs text-emerald-600 font-medium mb-1">Couvre jusqu'à 10m²*</p>
                <p className="text-slate-500 mb-6">Pour surveiller une zone clé (Salon ou Potager)</p>

                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-4xl font-extrabold text-slate-900">89€</span>
                    <span className="text-sm text-slate-400">achat unique</span>
                  </div>
                  <p className="text-xs text-emerald-600 font-medium">Expert Horticole inclus — aucun abonnement</p>
                </div>

                <ul className="space-y-4 mb-8 flex-grow">
                  {[
                    "1x Boîtier CynthIA (Int/Ext)",
                    "Expert Horticole inclus à vie",
                    "Diagnostic photo basé sur l'état de votre microclimat",
                    "Garantie 2 ans",
                    "Livraison OFFERTE",
                    "Piles fournies (+200j)",
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-600">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                        <Check className="w-3 h-3 text-emerald-600" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => {
                    navigate("/product/cynthia-cynthia");
                    window.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }}
                  className="w-full py-6 text-lg font-bold bg-slate-900 hover:bg-slate-800 text-white rounded-xl shadow-lg mt-auto transition-transform active:scale-95"
                >
                  Commander mon CynthIA
                </Button>
                <p className="text-center text-xs text-slate-400 mt-4 flex items-center justify-center gap-1">
                  <Shield className="w-3 h-3" /> Paiement 100% Sécurisé
                </p>
              </div>
            </motion.div>

            {/* CARTE 2 : DUO */}
            <motion.div
              className="bg-slate-900 rounded-3xl border-2 border-emerald-500 shadow-2xl relative overflow-hidden transform md:-translate-y-4 flex flex-col h-full"
              initial={{
                opacity: 0,
                x: 20,
              }}
              whileInView={{
                opacity: 1,
                x: 0,
              }}
              viewport={{
                once: true,
              }}
            >
              <div className="absolute top-0 right-0 bg-emerald-500 text-white text-xs font-bold px-4 py-2 rounded-bl-xl z-10 shadow-lg">
                BEST SELLER
              </div>

              <div className="h-48 bg-slate-800/50 flex items-center justify-center border-b border-slate-700 relative overflow-hidden">
                <LazyVideo src={packDuoAsset.url} className="w-full h-full" />
              </div>

              <div className="p-6 md:p-8 flex flex-col flex-grow">
                <h3 className="text-2xl font-bold text-white mb-2">Pack Jardin Complet</h3>
                <p className="text-xs text-emerald-400 font-medium mb-1">Couvre jusqu'à 10m² par boîtier*</p>
                <p className="text-emerald-100/80 mb-6">
                  Idéal pour vos grandes plantations ou pour vos zones Intérieur & Extérieur
                </p>

                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-5xl font-extrabold text-white">159€</span>
                    <span className="text-sm text-slate-400">achat unique</span>
                  </div>
                  <p className="text-xs text-emerald-400 font-medium">Économisez 19€</p>
                </div>

                <p className="text-sm text-emerald-300/90 italic mb-4">Tout du Pack Découverte, plus :</p>
                <ul className="space-y-4 mb-8 flex-grow">
                  {[
                    "2x Boîtiers CynthIA",
                    "Gestion Multi-Zones",
                    "Ebook sur le jardinage 3.0 offert",
                    "2x Piles fournies (+200j/boîtier)",
                    "Économisez 19€",
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-300">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <Check className="w-3 h-3 text-emerald-400" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => {
                    navigate("/product/pack-duo");
                    window.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }}
                  className="w-full py-6 text-lg font-bold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-[0_0_20px_rgba(16,185,129,0.3)] mt-auto transition-transform active:scale-95"
                >
                  Choisir le Pack Duo
                </Button>
                <p className="text-center text-xs text-slate-500 mt-4">Économisez par rapport à 2 packs solo</p>
              </div>
            </motion.div>
          </div>

          {/* BARRE DE CONFIANCE */}
          <div className="max-w-5xl mx-auto bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 mb-1">
                  <Shield className="w-5 h-5" />
                </div>
                <h4 className="font-bold text-sm md:text-base text-slate-900">Garantie 2 Ans</h4>
                <p className="text-xs text-slate-500 hidden md:block">Pièces et main d'œuvre incluses</p>
              </div>

              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mb-1">
                  <Truck className="w-5 h-5" />
                </div>
                <h4 className="font-bold text-sm md:text-base text-slate-900">Livraison Gratuite</h4>
                <p className="text-xs text-slate-500 hidden md:block">Expédition rapide sous 24h</p>
              </div>

              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 mb-1">
                  <Heart className="w-5 h-5" />
                </div>
                <h4 className="font-bold text-sm md:text-base text-slate-900">Satisfait ou Remboursé</h4>
                <p className="text-xs text-slate-500 hidden md:block">30 jours pour changer d'avis</p>
              </div>

              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 mb-1">
                  <HeadphonesIcon className="w-5 h-5" />
                </div>
                <h4 className="font-bold text-sm md:text-base text-slate-900">Support 24/7</h4>
                <p className="text-xs text-slate-500 hidden md:block">Une équipe d'experts à votre écoute</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FAQ SECTION ================= */}
      <FAQSection />

      <Footer />
    </div>
  );
};
export default Index;
